import React, {FC, useEffect, useState} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {
  AnalyticsCreateExportOptionsModel,
  AsyncExportTypesEnum,
  EventTypesEnum,
} from '../../../models/api/analyticsExportModel';
import {InterfaceEventsEnum} from '../../../services/tracking';
import {updateAnalyticsExportsColumnsAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getAnalyticsExportsColumns} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {
  createAnalyticsExportAsyncAction,
  fetchAnalyticsColumnNames,
  fetchAnalyticsExports,
} from '../../../store/panels/analytics/exports/analyticsPanelExportsActions';
import {
  getAnalyticsCsatColumnNames,
  getAnalyticsCsatExportsList,
  getIsCsatColumnsLoaded,
} from '../../../store/panels/analytics/exports/analyticsPanelExportsSelectors';
import {getAnalyticsFilters} from '../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {mapFiltersToTerms} from '../../helpers/analyticsSavedViewsHelpers';
import {useAnalyticsTracking} from '../../helpers/tracking/useAnalyticsTracking';
import {separatorConfigs} from './helpers/common';
import {ExportColumnsInput} from './helpers/exportColumnsInput';
import {ExportInfoText} from './helpers/exportInfoText';
import {ExportItemList} from './helpers/exportItemList';
import {ExportSeparatorInput} from './helpers/exportSeparatorInput';
import {ExportTypeInput} from './helpers/exportTypeInput';

/*
 * Props.
 */

interface CsatExportDialogProps {
  onRequestClose: Handler;
}

/*
 * Constants.
 */

const exportType = {
  value: AsyncExportTypesEnum.CSAT,
  title: <Trans id="analytics-exports-csat-export-title">Customer satisfaction data</Trans>,
};
const availableEventTypes = [EventTypesEnum.COMMENT];

/*
 * Component.
 */

export const CsatExportDialog: FC<CsatExportDialogProps> = ({onRequestClose}) => {
  const dispatch = useAppDispatch();
  const [selectedSeparator, setSelectedSeparator] = useState(separatorConfigs[0]);
  const [selectedColumns, setSelectedColumns] = useState<ReadonlyArray<string>>([]);

  const isLoaded = useAppSelector(getIsCsatColumnsLoaded);
  const availableColumns = useAppSelector(getAnalyticsCsatColumnNames);
  const csatExportList = useAppSelector(getAnalyticsCsatExportsList);
  const filters = useAppSelector(getAnalyticsFilters);
  const trackAnalytics = useAnalyticsTracking();

  // These are the columns previously selected by the user for both the messages and activities export type.
  // NOTE: This is a local preference, not one which comes from the API.
  const analyticsExportsColumnsPreference = useAppSelector(getAnalyticsExportsColumns);

  useInitialEffect(() => {
    if (!isLoaded) {
      dispatch(fetchAnalyticsColumnNames());
      dispatch(fetchAnalyticsExports());
    }
  });

  // pre-select all the available columns.
  useEffect(() => {
    if (isLoaded) {
      setSelectedColumns(analyticsExportsColumnsPreference?.csatColumns ?? availableColumns);
    }
  }, [isLoaded, availableColumns, analyticsExportsColumnsPreference?.csatColumns]);

  const onAsyncExportRequestClick = () => {
    const exportOptions: AnalyticsCreateExportOptionsModel = {
      export_type: exportType.value,
      sep: selectedSeparator.value,
      columns: selectedColumns,
      event_types: availableEventTypes,
      filters: {terms: mapFiltersToTerms(filters)},
    };

    dispatch(createAnalyticsExportAsyncAction(exportOptions));
    dispatch(updateAnalyticsExportsColumnsAction(exportOptions.export_type, exportOptions.columns));

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_EXPORT_CSAT);
  };

  return (
    <OkCancelDialog
      title={<Trans id="csat-export-dialog-title">Export data</Trans>}
      okButtonLabel={<Trans id="async-export-request-button">Request export</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={onRequestClose}
      onOkClick={onAsyncExportRequestClick}
      width={500}
    >
      <ExportTypeInput
        availableExportTypes={[exportType]}
        selectedExportType={exportType}
        isLoading={!isLoaded}
      />
      <ExportSeparatorInput
        separatorConfigs={separatorConfigs}
        selectedSeparator={selectedSeparator}
        setSelectedSeparator={setSelectedSeparator}
        isLoading={!isLoaded}
      />
      <ExportColumnsInput
        availableColumns={availableColumns}
        selectedColumns={selectedColumns}
        setColumns={setSelectedColumns}
        isLoading={!isLoaded}
      />
      <ExportInfoText
        isLoading={!isLoaded}
        text={
          <Trans id="csat-export-info-text">
            Exports are CSV files with the raw data used in the analytics dashboard. Export links will expire
            after 30 days.
          </Trans>
        }
        subtext={<Trans id="csat-export-info-subtext">View documentation</Trans>}
      />
      <ExportItemList exportItems={csatExportList} isLoading={!isLoaded} />
    </OkCancelDialog>
  );
};
